import { useState, useCallback, useMemo, useEffect } from 'react';
import { 
  LabelTemplate, 
  LabelElement, 
  FieldData, 
  LABEL_WIDTH_MM, 
  LABEL_HEIGHT_MM 
} from '@/types/label';

const WORKING_TEMPLATE_KEY = 'label-forge-working-template';
const WORKING_FIELD_DATA_KEY = 'label-forge-working-field-data';

const defaultTemplate: LabelTemplate = {
  id: 'default',
  name: 'Default Label',
  width: LABEL_WIDTH_MM,
  height: LABEL_HEIGHT_MM,
  elements: [
    {
      id: 'title',
      type: 'text',
      position: { x: 2, y: 2 },
      size: { width: 30, height: 5 },
      content: '{{productName}}',
      fontSize: 10,
      fontFamily: 'Arial',
      fontWeight: 'bold',
      textAlign: 'left',
      field: 'productName',
    },
    {
      id: 'barcode',
      type: 'barcode',
      position: { x: 2, y: 12 },
      size: { width: 30, height: 10 },
      content: '{{sku}}',
      field: 'sku',
      includeText: true,
    },
    {
      id: 'qr',
      type: 'qrcode',
      position: { x: 35, y: 2 },
      size: { width: 12, height: 12 },
      content: '{{url}}',
      field: 'url',
    },
  ],
  createdAt: Date.now(),
  updatedAt: Date.now(),
};

const defaultFieldData: FieldData = {
  productName: 'Sample Product',
  sku: '123456789012',
  url: 'https://example.com',
};

// Load initial template from localStorage
const loadStoredTemplate = (): LabelTemplate => {
  try {
    const stored = localStorage.getItem(WORKING_TEMPLATE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.elements && Array.isArray(parsed.elements)) {
        return { 
          ...parsed, 
          width: parsed.width || LABEL_WIDTH_MM, 
          height: parsed.height || LABEL_HEIGHT_MM 
        };
      }
    }
  } catch (e) {
    console.error('Failed to load working template:', e);
  }
  return defaultTemplate;
};

// Load initial field data from localStorage
const loadStoredFieldData = (): FieldData => {
  try {
    const stored = localStorage.getItem(WORKING_FIELD_DATA_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load field data:', e);
  }
  return defaultFieldData;
};

export const useTemplate = () => {
  const [template, setTemplate] = useState<LabelTemplate>(loadStoredTemplate);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const [fieldData, setFieldData] = useState<FieldData>(loadStoredFieldData);

  // Auto-save template to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(WORKING_TEMPLATE_KEY, JSON.stringify(template));
    } catch (e) {
      console.error('Failed to save working template:', e);
    }
  }, [template]);

  // Auto-save field data to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem(WORKING_FIELD_DATA_KEY, JSON.stringify(fieldData));
    } catch (e) {
      console.error('Failed to save field data:', e);
    }
  }, [fieldData]);

  // Extract fields from template - deduplicate when field property and {{field}} in content match
  const detectedFields = useMemo(() => {
    const fields = new Set<string>();
    template.elements.forEach((element) => {
      // If element has a field property, use that as the canonical field name
      if (element.field) {
        fields.add(element.field);
      }
      // Also detect {{field}} patterns in content, but only add if not already covered by field property
      if (element.content) {
        const matches = element.content.match(/\{\{([^}]+)\}\}/g);
        if (matches) {
          matches.forEach((match) => {
            const fieldName = match.replace(/\{\{|\}\}/g, '');
            // Only add if it's different from the element's field property
            // This prevents duplicates when both {{productName}} and field="productName" exist
            if (!element.field || fieldName !== element.field) {
              fields.add(fieldName);
            }
          });
        }
      }
    });
    return Array.from(fields);
  }, [template]);

  // Get selected element
  const selectedElement = useMemo(() => {
    return template.elements.find((el) => el.id === selectedElementId) || null;
  }, [template, selectedElementId]);

  // Add new element
  const addElement = useCallback((type: LabelElement['type']) => {
    const id = `${type}-${Date.now()}`;
    let newElement: LabelElement;

    switch (type) {
      case 'text':
        newElement = {
          id,
          type: 'text',
          position: { x: 5, y: 5 },
          size: { width: 20, height: 5 },
          content: 'New Text',
          fontSize: 10,
          fontFamily: 'Arial',
          fontWeight: 'normal',
          textAlign: 'left',
        };
        break;
      case 'qrcode':
        newElement = {
          id,
          type: 'qrcode',
          position: { x: 5, y: 5 },
          size: { width: 10, height: 10 },
          content: 'https://example.com',
        };
        break;
      case 'barcode':
        newElement = {
          id,
          type: 'barcode',
          position: { x: 5, y: 5 },
          size: { width: 25, height: 8 },
          content: '123456789',
          includeText: true,
        };
        break;
      case 'image':
        newElement = {
          id,
          type: 'image',
          position: { x: 5, y: 5 },
          size: { width: 10, height: 10 },
          src: '',
          objectFit: 'contain',
        };
        break;
      case 'shape':
        newElement = {
          id,
          type: 'shape',
          position: { x: 5, y: 5 },
          size: { width: 10, height: 10 },
          shapeType: 'rectangle',
          fillColor: 'transparent',
          strokeColor: '#000000',
          strokeWidth: 1,
        };
        break;
      case 'icon':
        newElement = {
          id,
          type: 'icon',
          position: { x: 5, y: 5 },
          size: { width: 5, height: 5 },
          iconName: 'star',
          color: '#000000',
        };
        break;
      default:
        return;
    }

    setTemplate((prev) => ({
      ...prev,
      elements: [...prev.elements, newElement],
      updatedAt: Date.now(),
    }));
    setSelectedElementId(id);
  }, []);

  // Update element
  const updateElement = useCallback((id: string, updates: Partial<LabelElement>) => {
    setTemplate((prev) => ({
      ...prev,
      elements: prev.elements.map((el) =>
        el.id === id ? { ...el, ...updates } as LabelElement : el
      ),
      updatedAt: Date.now(),
    }));
  }, []);

  // Delete element
  const deleteElement = useCallback((id: string) => {
    setTemplate((prev) => ({
      ...prev,
      elements: prev.elements.filter((el) => el.id !== id),
      updatedAt: Date.now(),
    }));
    if (selectedElementId === id) {
      setSelectedElementId(null);
    }
  }, [selectedElementId]);

  // Duplicate element
  const duplicateElement = useCallback((id: string) => {
    const element = template.elements.find((el) => el.id === id);
    if (!element) return;

    const newId = `${element.type}-${Date.now()}`;
    const duplicate: LabelElement = {
      ...JSON.parse(JSON.stringify(element)),
      id: newId,
      position: {
        x: Math.min(element.position.x + 2, template.width - element.size.width),
        y: Math.min(element.position.y + 2, template.height - element.size.height),
      },
    };

    setTemplate((prev) => ({
      ...prev,
      elements: [...prev.elements, duplicate],
      updatedAt: Date.now(),
    }));
    setSelectedElementId(newId);
  }, [template.elements, template.width, template.height]);

  // Reorder element
  const reorderElement = useCallback((id: string, direction: 'up' | 'down') => {
    setTemplate((prev) => {
      const elements = [...prev.elements];
      const index = elements.findIndex((el) => el.id === id);
      if (index === -1) return prev;

      const newIndex = direction === 'up' ? index + 1 : index - 1;
      if (newIndex < 0 || newIndex >= elements.length) return prev;

      [elements[index], elements[newIndex]] = [elements[newIndex], elements[index]];
      
      return {
        ...prev,
        elements,
        updatedAt: Date.now(),
      };
    });
  }, []);

  // Move element from one index to another (for drag and drop)
  const moveElement = useCallback((fromIndex: number, toIndex: number) => {
    setTemplate((prev) => {
      if (fromIndex === toIndex) return prev;
      if (fromIndex < 0 || fromIndex >= prev.elements.length) return prev;
      if (toIndex < 0 || toIndex >= prev.elements.length) return prev;

      const elements = [...prev.elements];
      const [removed] = elements.splice(fromIndex, 1);
      elements.splice(toIndex, 0, removed);
      
      return {
        ...prev,
        elements,
        updatedAt: Date.now(),
      };
    });
  }, []);

  // Update template from JSON
  const updateTemplateFromJson = useCallback((json: string) => {
    try {
      const parsed = JSON.parse(json);
      if (parsed.elements && Array.isArray(parsed.elements)) {
        setTemplate({
          ...parsed,
          id: parsed.id || `template-${Date.now()}`,
          width: parsed.width || template.width,
          height: parsed.height || template.height,
          createdAt: parsed.createdAt || Date.now(),
          updatedAt: Date.now(),
        });
        return true;
      }
    } catch (e) {
      console.error('Invalid JSON:', e);
    }
    return false;
  }, [template.width, template.height]);

  // Update label size
  const updateLabelSize = useCallback((width: number, height: number) => {
    setTemplate((prev) => ({
      ...prev,
      width,
      height,
      updatedAt: Date.now(),
    }));
  }, []);

  // Load template
  const loadTemplate = useCallback((newTemplate: LabelTemplate) => {
    setTemplate({
      ...newTemplate,
      width: newTemplate.width || LABEL_WIDTH_MM,
      height: newTemplate.height || LABEL_HEIGHT_MM,
    });
    setSelectedElementId(null);
    
    // Update field data with template's detected fields
    const newFieldData: FieldData = { ...fieldData };
    newTemplate.elements.forEach((element) => {
      if (element.field && !newFieldData[element.field]) {
        newFieldData[element.field] = '';
      }
    });
    setFieldData(newFieldData);
  }, [fieldData]);

  // Update field data
  const updateFieldData = useCallback((field: string, value: string) => {
    setFieldData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  // Bulk update field data (for CSV)
  const setAllFieldData = useCallback((data: FieldData) => {
    setFieldData((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  // Resolve content with field data
  const resolveContent = useCallback((content: string): string => {
    return content.replace(/\{\{([^}]+)\}\}/g, (match, fieldName) => {
      return fieldData[fieldName] || match;
    });
  }, [fieldData]);

  return {
    template,
    setTemplate,
    selectedElementId,
    setSelectedElementId,
    selectedElement,
    addElement,
    updateElement,
    deleteElement,
    duplicateElement,
    reorderElement,
    moveElement,
    updateTemplateFromJson,
    loadTemplate,
    updateLabelSize,
    detectedFields,
    fieldData,
    updateFieldData,
    setAllFieldData,
    resolveContent,
  };
};
